/* ISMS 웹 클라이언트 싱글톤 (2026-09-15) — 서버 계약: sales-server docs/isms/endpoints.md
 *
 * 역할
 *  1) graphql_headers: 모든 AppSync 요청에 x-gs-client / x-gs-active(최근 60초 입력 있으면 "1") — 서버 유휴 판정의 '활동' 신호
 *  2) AWSAPI.graphql 인터셉트: [ISMS_LOCKED]/[ISMS_IP_BLOCKED] 응답 → redux, 서버를 거치지 않는 @model 직결 조회 → LS_ismsAccess(log) DETAIL 통보
 *  3) session.check/touch/extend + 유휴 타이머(ISMS 정책이 켜진 계정에서만 리스너·루프 생성)
 *  4) 주문내역 CSV 다운로드 통보(logDownload)
 *  5) OTP 로그인(CUSTOM_AUTH): signIn → CUSTOM_CHALLENGE 면 전역 모달 상태로, answer/resend/신뢰기기
 *
 * 대시보드 호스트(dashboard*.thegrowing.co, localhost)에서만 동작. 마켓·웨이팅 호스트는 헤더도 붙이지 않는다.
 * actions/index.js 를 import 하지 않는다(순환 방지) — 상수는 src/isms/constants.js.
 */
import { API as AWSAPI, Auth, graphqlOperation } from "aws-amplify";
import * as mutations from "../graphql/mutations";
import * as C from "../isms/constants";
import { getTrustedDevice, setTrustedDevice, removeTrustedDevice } from "../isms/trustedDevices";

const HEADERS_ENABLED = process.env.REACT_APP_ISMS_HEADERS !== "0"; // CORS 문제 시 "0" 으로 헤더 비활성(킬스위치)
const ACTIVITY_WINDOW_MS = 60 * 1000;
const INPUT_THROTTLE_MS = 1000;
const LOOP_MS = 10 * 1000;
const HEARTBEAT_MS = 60 * 1000;
const FLUSH_MS = 2000;
const FLUSH_MAX = 50;
const DEDUP_MS = 30 * 1000;
const QUEUE_CAP = 200;
const TRUSTED_DAYS = 30;
const LS_INPUT_KEY = "gs_isms_input"; // 탭 간 활동 공유
const LS_LOCK_KEY = "gs_isms_lock"; // 탭 간 잠금 전파

// 서버 미경유(@model 직결) 조회 중 정보주체 열람으로 통보할 GraphQL operation (custom.js/queries.js 의 operation 이름 기준)
const DIRECT_AUDIT = {
  GetOrder: { field: "getOrder", subject: (v, d) => d && d.getOrder && d.getOrder.orderCustomerId, meta: (v) => ({ orderId: v.id }) },
  GetStoreCustomer: { field: "getStoreCustomer", subject: (v) => v.id },
  ListOrdersByStoreCustomer: { field: "listOrdersByStoreCustomer", subject: (v) => v.orderCustomerId },
  ListOrdersByCustomer: { field: "listOrdersByCustomer", subject: (v) => v.customerId },
  ListPointTransactionsByStoreCustomer: { field: "listPointTransactionsByStoreCustomer", subject: (v) => v.pointTransactionCustomerId },
  listCreditTransactionsByStoreCustomer: { field: "listCreditTransactionsByStoreCustomer", subject: (v) => v.creditTransactionCustomerId },
  ListCouponTransactionsByStoreCustomerMethod: { field: "listCouponTransactionsByStoreCustomer", subject: (v) => v.couponTransactionCustomerId },
};

const now = () => Date.now();
const opName = (query) => {
  if (typeof query !== "string") return null;
  const m = /^\s*(?:query|mutation|subscription)\s+([A-Za-z0-9_]+)/.exec(query);
  return m ? m[1] : null;
};

export const isDashboardHost = () => {
  const h = window.location.hostname;
  return h.includes("dashboard") || h === "localhost" || h === "127.0.0.1";
};

class IsmsClient {
  constructor() {
    this.store = null;
    this.installed = false;
    this.identity = { sub: null, email: null };
    this.storeId = null;
    this.status = null; // 마지막 session.check 응답
    this.lastInputAt = 0;
    this.lastActiveSentAt = 0; // x-gs-active:1 을 실은 마지막 요청 시각
    this.deadline = 0;
    this.warned = false;
    this.timers = { loop: null, heartbeat: null, flush: null };
    this.listening = false;
    this.queue = [];
    this.recent = new Map(); // dedup key -> ts
    this.pending = null; // OTP 진행 중 { user(CognitoUser), email, password, meta, mode, onSuccess }
    this._orig = null;
  }

  // ------------------------------------------------------------------ 공통
  attachStore(store) {
    this.store = store;
  }

  dispatch(action) {
    if (this.store) this.store.dispatch(action);
  }

  setIdentity({ sub, email }) {
    this.identity = { sub: sub || null, email: email || null };
  }

  enabled() {
    return isDashboardHost();
  }

  customAuthEnabled() {
    // 트리거(Define/Create/Verify) 가 풀에 부착되기 전에 켜면 로그인이 깨진다 — 배포 순서는 runbook §5.
    if (!isDashboardHost()) return false;
    try {
      if (window.localStorage.getItem("gs_isms_custom_auth") === "1") return true;
    } catch (e) {
      /* ignore */
    }
    return process.env.REACT_APP_ISMS_CUSTOM_AUTH === "1";
  }

  configureAuthFlow() {
    // 로그인 직전마다 다시 평가 — localStorage 스위치를 바꾼 뒤 새로고침 없이 로그아웃/로그인해도 반영되게.
    // (Auth.configure 는 병합이고 플로우는 signIn 시점에 CognitoUser 에 실린다)
    Auth.configure({ authenticationFlowType: this.customAuthEnabled() ? "CUSTOM_AUTH" : "USER_SRP_AUTH" });
  }

  // 초대(임시 비밀번호)·가입 확인 화면은 SRP 로 강제 — CUSTOM_AUTH 로 시작하면 Define 트리거가 세션을 판정한다
  async withSrp(fn) {
    Auth.configure({ authenticationFlowType: "USER_SRP_AUTH" });
    try {
      return await fn();
    } finally {
      this.configureAuthFlow();
    }
  }

  // Amplify.configure({...awsconfig, graphql_headers}) 에 넘길 함수
  graphqlHeaders = async () => {
    if (!HEADERS_ENABLED || !this.enabled()) return {};
    const active = now() - this.lastInputAt < ACTIVITY_WINDOW_MS;
    if (active) this.lastActiveSentAt = now();
    return { "x-gs-client": "dashboard", "x-gs-active": active ? "1" : "0" };
  };

  // AWSAPI.graphql 을 한 번 감싼다(구독 Observable 은 그대로 통과)
  install() {
    if (this.installed || !this.enabled()) return;
    const orig = AWSAPI.graphql.bind(AWSAPI);
    this._orig = orig;
    const self = this;
    AWSAPI.graphql = function (options, ...rest) {
      const res = orig(options, ...rest);
      if (!res || typeof res.then !== "function") return res;
      const name = options && opName(options.query);
      return res.then(
        (data) => {
          if (name && DIRECT_AUDIT[name]) self._auditDirect(name, options.variables || {}, data && data.data);
          return data;
        },
        (err) => {
          self.inspectError(err);
          throw err;
        },
      );
    };
    this.installed = true;
  }

  inspectError(err) {
    const msg = err && err.errors && err.errors[0] && err.errors[0].message;
    const m = typeof msg === "string" && C.ISMS_ERROR_RE.exec(msg);
    if (!m) return null;
    const code = m[1];
    if (code === "ISMS_LOCKED") {
      const r = /reason=([A-Z_]+)/.exec(m[2]);
      this.onLocked(r ? r[1] : "IDLE");
    } else if (code === "ISMS_IP_BLOCKED") {
      this.dispatch({ type: C.ISMS_BLOCKED, message: m[2], contact: this.status && this.status.ip && this.status.ip.contact });
    }
    return code;
  }

  async access(action, payload, storeId) {
    const call = this._orig || AWSAPI.graphql.bind(AWSAPI);
    const r = await call(
      graphqlOperation(mutations.LS_ismsAccess, {
        storeId: storeId || this.storeId || null,
        action,
        payload: JSON.stringify(payload || {}),
      }),
    );
    const v = r && r.data && r.data.LS_ismsAccess;
    return typeof v === "string" ? JSON.parse(v) : v;
  }

  // ------------------------------------------------------------------ 접속기록 통보(log)
  auditEnabled() {
    const a = this.status && this.status.audit;
    return !!(a && a.enabled && a.clientEvents !== false);
  }

  _auditDirect(name, vars, data) {
    const spec = DIRECT_AUDIT[name];
    let subjectId = null;
    try {
      subjectId = spec.subject(vars, data) || null;
    } catch (e) {
      subjectId = null;
    }
    const meta = spec.meta ? spec.meta(vars, data) : {};
    this.logEvent({ action: "DETAIL", field: spec.field, subjectId, meta });
  }

  logEvent({ action, field, subjectId, query, subjectCount, meta }) {
    if (!this.enabled()) return;
    if (this.status && !this.auditEnabled()) return; // 정책 확인 후 꺼져 있으면 버림(확인 전엔 버퍼)
    const key = `${action}:${field}:${subjectId || ""}`;
    const t = now();
    const prev = this.recent.get(key);
    if (prev && t - prev < DEDUP_MS && action !== "DOWNLOAD") return;
    this.recent.set(key, t);
    if (this.recent.size > 500) {
      for (const [k, ts] of this.recent) if (t - ts > DEDUP_MS) this.recent.delete(k);
    }
    const ev = {
      action,
      field,
      screen: (window.location.pathname || "").slice(0, 64),
      meta: { ...(meta || {}), clientAt: new Date(t).toISOString() },
    };
    if (subjectId) ev.subjectId = String(subjectId).slice(0, 64);
    if (query) ev.query = query;
    if (typeof subjectCount === "number") ev.subjectCount = subjectCount;
    if (this.queue.length >= QUEUE_CAP) this.queue.shift();
    this.queue.push(ev);
    if (!this.timers.flush) this.timers.flush = setTimeout(() => this.flush(), FLUSH_MS);
  }

  logDownload({ field, fileName, rows, batch, kind, query, screen }) {
    this.logEvent({
      action: "DOWNLOAD",
      field: field || "downloadCSV",
      subjectCount: typeof rows === "number" ? rows : 0,
      query,
      meta: { fileName, rows, batch, kind: kind || "csv", screen },
    });
  }

  async flush() {
    clearTimeout(this.timers.flush);
    this.timers.flush = null;
    if (!this.queue.length || !this.identity.sub) return;
    if (this.status && !this.auditEnabled()) {
      this.queue = [];
      return;
    }
    const events = this.queue.splice(0, FLUSH_MAX);
    try {
      await this.access("log", { events });
    } catch (e) {
      console.debug("isms log failed", e && e.errors ? e.errors[0] : e);
    }
    if (this.queue.length && !this.timers.flush) this.timers.flush = setTimeout(() => this.flush(), FLUSH_MS);
  }

  // ------------------------------------------------------------------ 세션·유휴
  async start({ storeId }) {
    if (!this.enabled()) return null;
    this.storeId = storeId || this.storeId;
    if (!this.listening) {
      document.addEventListener("visibilitychange", this._onVisibility);
      window.addEventListener("storage", this._onStorage);
      this.listening = true;
    }
    return this.check();
  }

  async check() {
    if (!this.enabled()) return null;
    let s;
    try {
      s = await this.access("session.check", {});
    } catch (e) {
      console.debug("isms session.check failed", e && e.errors ? e.errors[0] : e);
      return null;
    }
    this.applyStatus(s);
    return s;
  }

  applyStatus(s) {
    if (!s) return;
    this.status = s;
    this.dispatch({ type: C.ISMS_STATUS_RECEIVED, status: s });
    if (s.blocked) {
      this.dispatch({ type: C.ISMS_BLOCKED, message: null, contact: s.ip && s.ip.contact });
    }
    if (s.locked) {
      this.onLocked(s.reason || "IDLE");
      return;
    }
    if (s.idle && s.idle.enabled) {
      this._syncDeadline(s);
      this._startIdle();
    } else {
      this._stopIdle();
    }
    if (this.auditEnabled() && this.queue.length) this.flush();
  }

  // 경고 표시 = warned 불리언 + redux 를 항상 함께 (한쪽만 바뀌면 모달이 0:00 에 남는다)
  _setWarning(remainSec) {
    const on = remainSec != null;
    this.warned = on;
    this.dispatch({ type: C.ISMS_IDLE_WARNING, warning: on ? { remainingSec: remainSec } : null });
  }

  _syncDeadline(s) {
    const remain = s.idle && typeof s.idle.remainingSec === "number" ? s.idle.remainingSec : null;
    if (remain === null) return;
    this.deadline = now() + remain * 1000; // serverNow 기준 오프셋만 사용(클라 시계 절대값 무관)
    if (remain * 1000 > this._warnMs()) this._setWarning(null); // 연장됨 → 모달도 해제
  }

  _timeoutMs() {
    const m = this.status && this.status.idle && this.status.idle.timeoutMin;
    return (m || 60) * 60 * 1000;
  }

  _warnMs() {
    const m = this.status && this.status.idle && this.status.idle.warnBeforeMin;
    return (m || 5) * 60 * 1000;
  }

  _startIdle() {
    if (this.timers.loop) return;
    ["mousemove", "mousedown", "keydown", "touchstart", "wheel"].forEach((ev) =>
      window.addEventListener(ev, this._onInput, { passive: true }),
    );
    this.timers.loop = setInterval(() => this._tick(), LOOP_MS);
    this.timers.heartbeat = setInterval(() => this._heartbeat(), HEARTBEAT_MS);
  }

  _stopIdle() {
    ["mousemove", "mousedown", "keydown", "touchstart", "wheel"].forEach((ev) =>
      window.removeEventListener(ev, this._onInput, { passive: true }),
    );
    clearInterval(this.timers.loop);
    clearInterval(this.timers.heartbeat);
    this.timers.loop = null;
    this.timers.heartbeat = null;
    this.warned = false;
  }

  _onInput = () => {
    const t = now();
    if (this.warned) return; // 경고가 뜬 뒤에는 입력을 활동으로 보지 않는다 — '계속 사용하기' 클릭(extend)만 연장(우진 결정 2026-09-16)
    if (t - this.lastInputAt < INPUT_THROTTLE_MS) return;
    this.lastInputAt = t;
    if (this.timers.loop) {
      // 입력이 있으면 만료가 계속 밀린다(서버는 x-gs-active 요청/touch 로 따라온다)
      this.deadline = t + this._timeoutMs();
    }
    try {
      window.localStorage.setItem(LS_INPUT_KEY, String(t));
    } catch (e) {
      /* ignore */
    }
  };

  _onStorage = (e) => {
    if (!e) return;
    if (e.key === LS_INPUT_KEY && e.newValue) {
      const t = Number(e.newValue) || 0;
      if (t > this.lastInputAt) {
        this.lastInputAt = t;
        if (this.timers.loop) this.deadline = Math.max(this.deadline, t + this._timeoutMs());
      }
    } else if (e.key === LS_LOCK_KEY && e.newValue) {
      this.check();
    }
  };

  _onVisibility = () => {
    if (document.visibilityState === "visible" && this.status && (this.status.idle || {}).enabled) this.check();
  };

  async _tick() {
    if (!this.timers.loop) return;
    const remain = this.deadline - now();
    if (remain <= 0) {
      const s = await this.check(); // 서버 판정이 정답(다른 탭 활동 등)
      if (s && !s.locked) this.warned = false;
      return;
    }
    if (remain <= this._warnMs()) {
      this._setWarning(Math.ceil(remain / 1000));
    } else {
      this._setWarning(null); // 만료가 밀렸으면(다른 탭 활동·extend) 경고 해제 — warned 상태와 무관하게 redux 정리
    }
  }

  // 모달 카운트다운이 0 에 닿으면 즉시 서버 판정(10초 루프 대기 없이). 경계(서버가 아직 timeout 직전)면 잠깐 뒤 재시도.
  async enforceIdle() {
    if (!this.timers.loop) return;
    const s = await this.check();
    if (s && !s.locked && this.warned) {
      setTimeout(() => {
        if (this.warned && this.timers.loop) this.check();
      }, 2500);
    }
  }

  async _heartbeat() {
    if (!this.timers.loop || this.warned) return; // 경고 표시 중에는 stale 입력으로 세션을 연장하지 않는다(0:00 고착 방지)
    const t = now();
    const hadInput = t - this.lastInputAt < HEARTBEAT_MS;
    const sentActive = t - this.lastActiveSentAt < HEARTBEAT_MS;
    if (hadInput && !sentActive) {
      try {
        const s = await this.access("session.touch", {});
        if (s) {
          this.status = s;
          if (s.locked) this.onLocked(s.reason || "IDLE");
          else this._syncDeadline(s);
        }
      } catch (e) {
        /* 다음 주기 */
      }
    }
  }

  async extend() {
    try {
      const s = await this.access("session.extend", {});
      this.lastInputAt = now();
      this.deadline = now() + this._timeoutMs();
      this._setWarning(null);
      this.applyStatus(s);
      return s;
    } catch (e) {
      return null;
    }
  }

  onLocked(reason) {
    this._stopIdle();
    this.dispatch({ type: C.ISMS_LOCKED, reason });
    try {
      window.localStorage.setItem(LS_LOCK_KEY, String(now()));
    } catch (e) {
      /* ignore */
    }
  }

  stop() {
    this._stopIdle();
    if (this.listening) {
      document.removeEventListener("visibilitychange", this._onVisibility);
      window.removeEventListener("storage", this._onStorage);
      this.listening = false;
    }
    clearTimeout(this.timers.flush);
    this.timers.flush = null;
    this.queue = [];
    this.status = null;
    this.storeId = null;
    this.identity = { sub: null, email: null };
    this.pending = null;
    this.dispatch({ type: C.ISMS_RESET });
  }

  // ------------------------------------------------------------------ OTP 로그인
  // trustedDevice 는 입력한 이메일의 항목만(계정별 쿠키, isms/trustedDevices.js). 항목이 없으면 키 자체를 넣지 않는다.
  async _clientMetadata(email, extra) {
    const meta = { client: "dashboard", ...(extra || {}) };
    const td = await getTrustedDevice(email);
    if (td) meta.trustedDevice = td;
    return meta;
  }

  _accountEmail(email) {
    return email || this.identity.email || (this.pending && this.pending.email) || null;
  }

  /** 대시보드 로그인. 반환 {challenge:true}(OTP 모달로 진행) 또는 {user}. mode: 'signin' | 'unlock' */
  async signIn(email, password, { mode = "signin", onSuccess, unlock } = {}) {
    this.configureAuthFlow();
    const meta = await this._clientMetadata(email, unlock ? { unlock: "1" } : null);
    const user = await Auth.signIn(email, password, meta);
    if (user && user.challengeName === "CUSTOM_CHALLENGE") {
      this.pending = { user, email, password, meta, mode, onSuccess };
      this.dispatch({ type: C.ISMS_OTP_REQUIRED, otp: { mode, ...this._otpView(user.challengeParam) } });
      return { challenge: true };
    }
    if (onSuccess) await onSuccess(user); // 후속(session.check·라우팅)까지 끝나야 호출자의 로딩이 풀린다
    return { user };
  }

  _otpView(p) {
    p = p || {};
    return {
      email: p.email || "",
      reason: p.reason || "OTP_SENT",
      attemptsLeft: Number(p.attemptsLeft || 0),
      retryAfterSec: Number(p.retryAfterSec || 0),
      expiresInSec: Number(p.expiresInSec || 0),
    };
  }

  async answerOtp(code, trustDevice) {
    const pending = this.pending;
    if (!pending) return { done: false, error: "세션이 만료되었습니다. 다시 로그인해 주세요." };
    this.dispatch({ type: C.ISMS_OTP_UPDATED, patch: { busy: true, error: null } });
    let user;
    try {
      user = await Auth.sendCustomChallengeAnswer(pending.user, String(code || "").trim(), pending.meta);
    } catch (err) {
      // 세션 내 3회 오답·세션 만료(3분) → NotAuthorizedException. 처음부터 다시.
      this.pending = null;
      this.dispatch({ type: C.ISMS_OTP_DONE });
      return { done: false, restart: true, error: "인증에 실패했습니다. 다시 로그인해 주세요." };
    }
    if (user && user.challengeName === "CUSTOM_CHALLENGE") {
      pending.user = user;
      const view = this._otpView(user.challengeParam);
      this.dispatch({ type: C.ISMS_OTP_UPDATED, patch: { ...view, busy: false, error: `인증코드가 올바르지 않습니다. (남은 횟수 ${view.attemptsLeft}회)` } });
      return { done: false };
    }
    // 토큰 발급 완료
    this.pending = null;
    if (trustDevice) await this.trustDevice(pending.email);
    this.dispatch({ type: C.ISMS_OTP_DONE });
    if (pending.onSuccess) await pending.onSuccess(user);
    return { done: true, user };
  }

  async resendOtp() {
    const pending = this.pending;
    if (!pending) return { error: "세션이 만료되었습니다. 다시 로그인해 주세요." };
    this.dispatch({ type: C.ISMS_OTP_UPDATED, patch: { busy: true, error: null } });
    try {
      const user = await Auth.signIn(pending.email, pending.password, pending.meta);
      if (user && user.challengeName === "CUSTOM_CHALLENGE") {
        pending.user = user;
        const view = this._otpView(user.challengeParam);
        const msg = view.reason === "OTP_COOLDOWN" ? `잠시 후 다시 시도해 주세요. (${view.retryAfterSec}초)` : null;
        this.dispatch({ type: C.ISMS_OTP_UPDATED, patch: { ...view, busy: false, error: msg } });
        return { reason: view.reason };
      }
      // 챌린지 없이 발급(정책이 꺼졌거나 신뢰기기) → 성공 처리
      this.pending = null;
      this.dispatch({ type: C.ISMS_OTP_DONE });
      if (pending.onSuccess) await pending.onSuccess(user);
      return { done: true };
    } catch (err) {
      const text = (err && err.message) || "";
      const token = /OTP_SEND_LIMIT|OTP_SEND_FAILED|OTP_EMAIL_SUPPRESSED/.exec(text);
      const msg = token
        ? { OTP_SEND_LIMIT: "인증 메일 발송 한도를 초과했습니다. 1시간 후 다시 시도해 주세요.", OTP_SEND_FAILED: "인증 메일 발송에 실패했습니다. 잠시 후 다시 시도해 주세요.", OTP_EMAIL_SUPPRESSED: "메일 주소로 발송할 수 없습니다. 관리자에게 문의해 주세요." }[token[0]]
        : "재발송에 실패했습니다. 다시 로그인해 주세요.";
      this.dispatch({ type: C.ISMS_OTP_UPDATED, patch: { busy: false, error: msg } });
      return { error: msg };
    }
  }

  cancelOtp() {
    this.pending = null;
    this.dispatch({ type: C.ISMS_OTP_DONE });
  }

  /** device.trust 응답을 해당 계정(인자 > identity.email > pending.email) 항목에만 기록. 다른 계정 항목은 유지. */
  async trustDevice(email) {
    const account = this._accountEmail(email);
    if (!account) return false;
    try {
      const r = await this.access("device.trust", { ua: (navigator.userAgent || "").slice(0, 256) });
      if (r && r.deviceId && r.token) {
        return setTrustedDevice(account, `${r.deviceId}:${r.token}`, r.trustedDeviceDays || TRUSTED_DAYS);
      }
    } catch (e) {
      console.debug("device.trust failed", e && e.errors ? e.errors[0] : e);
    }
    return false;
  }

  /** 해당 계정 항목만 삭제(로그아웃·device.revoke 용). 쿠키 전체는 지우지 않는다 — stop() 이 identity 를 비우므로 그 전에 호출. */
  forgetDevice(email) {
    const account = this._accountEmail(email);
    return account ? removeTrustedDevice(account) : Promise.resolve(false);
  }
}

const ismsClient = new IsmsClient();
export default ismsClient;
